import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Stack, Autocomplete
} from '@mui/material';
import { Add, Delete, Print } from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, query, orderBy, deleteDoc, doc, updateDoc, where, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488";

function Studentinpass() {
    const [inPasses, setInPasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [schoolId, setSchoolId] = useState(null);
    const [printData, setPrintData] = useState(null);

    const [formData, setFormData] = useState({
        studentId: '',
        studentName: '',
        gradeClass: '',
        parentsName: '',
        parentsContact: '',
        parentsAdhar: '',
        address: '',
        vehicleNo: '',
        emergencyNo: '',
        relation: '',
        date: new Date().toISOString().split('T')[0],
        day: new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date()),
        outTime: '',
    });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser) return;
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userSnap.exists()) {
                const sId = userSnap.data().schoolId;
                setSchoolId(sId);
                fetchBoardingStudents(sId);
                fetchInPasses(sId);
            }
        };
        init();
    }, []);

    const getDayFromDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
    };

    const fetchBoardingStudents = async (sId) => {
        try {
            const [admSnap, studentSnap, courseSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "courses"), where("schoolId", "==", sId)))
            ]);

            const studentMap = {};
            studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });
            const courseMap = {};
            courseSnap.docs.forEach(d => { courseMap[d.id] = d.data(); });

            const list = admSnap.docs.map((adm) => {
                const admData = adm.data();
                const sData = studentMap[admData.studentId];
                return {
                    id: admData.studentId,
                    name: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase(),
                    className: courseMap[admData.class]?.name || "N/A",
                    parentName: sData?.fathername || sData?.fatherName || '',
                    parentContact: sData?.fathermobile || '',
                    adhar: sData?.father_adharno || sData?.adharno || '',
                    address: sData?.address || '',
                    label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''} - ${courseMap[admData.class]?.name || ''}`.toUpperCase()
                };
            });
            setStudents(list);
        } catch (err) { console.error("Error fetching students:", err); }
    };

    const fetchInPasses = async (sId) => {
        const q = query(collection(db, "hostel_in_passes"), where("schoolId", "==", sId), orderBy("timestamp", "desc"));
        const snap = await getDocs(q);
        setInPasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const handleStudentSelect = (val) => {
        if (val) {
            setFormData(prev => ({
                ...prev,
                studentId: val.id,
                studentName: val.name,
                gradeClass: val.className,
                parentsName: val.parentName,
                parentsContact: val.parentContact,
                parentsAdhar: val.adhar,
                address: val.address
            }));
        }
    };

    const handleSave = async () => {
        if (!formData.studentId) return Swal.fire("Error", "Please select a student", "error");
        setLoading(true);
        try {
            const data = { ...formData, schoolId, timestamp: Timestamp.now() };
            const passNo = inPasses.length > 0 ? (Number(inPasses[0].passNo) + 1) : 101;
            await addDoc(collection(db, "hostel_in_passes"), { ...data, passNo });

            setOpen(false);
            fetchInPasses(schoolId);
            Swal.fire("Success", "In-Pass Saved", "success");
        } catch (e) { Swal.fire("Error", e.message, "error"); }
        setLoading(false);
    };

    const triggerPrint = (item) => {
        setPrintData(item);
        setTimeout(() => {
            window.print();
        }, 500);
    };

    return (
        <Box p={2} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <style>
                {`
                @media screen {
                    #print-area { display: none; }
                }
                @media print {
                    body * { visibility: hidden; }
                    #print-area, #print-area * { visibility: visible; }
                    #print-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        display: block !important;
                    }
                }
                .pass-container { 
                    border: 2px solid #000; 
                    padding: 20px; 
                    width: 210mm; 
                    font-family: 'Arial', sans-serif; 
                    background: #fff;
                }
                .dotted-line { 
                    border-bottom: 1px dotted #000; 
                    display: inline-block; 
                    min-width: 140px; 
                    padding: 0 5px; 
                    font-weight: bold; 
                }
                `}
            </style>

            <Stack direction="row" justifyContent="space-between" mb={3}>
                <Typography variant="h6" fontWeight="bold">Hostel Student In-Pass</Typography>
                <Button variant="contained" sx={{ bgcolor: PRIMARY }} startIcon={<Add />} onClick={() => setOpen(true)}>Generate In-Pass</Button>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell>Pass No</TableCell>
                            <TableCell>Student Name</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>In Date</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {inPasses.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.passNo}</TableCell>
                                <TableCell><b>{item.studentName}</b></TableCell>
                                <TableCell>{item.gradeClass}</TableCell>
                                <TableCell>{item.date} ({item.day})</TableCell>
                                <TableCell align="center">
                                    <IconButton color="primary" onClick={() => triggerPrint(item)}><Print /></IconButton>
                                    <IconButton color="error" onClick={async () => { await deleteDoc(doc(db, "hostel_in_passes", item.id)); fetchInPasses(schoolId); }}><Delete /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Hostel Student In-Pass Form</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 1 }}>
                        <Grid className="col-sm-4 mb-3" item xs={12}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(o) => o.label || ""}
                                onChange={(e, v) => handleStudentSelect(v)}
                                renderInput={(params) => <TextField {...params} label="Search Student" size="small" />}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={4}>
                            <TextField
                                fullWidth label="Date" type="date" size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value, day: getDayFromDate(e.target.value) })}
                            />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="Day (Auto)" size="small" value={formData.day} InputProps={{ readOnly: true }} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={4}><TextField fullWidth label="In Time" type="time" size="small" InputLabelProps={{ shrink: true }} value={formData.outTime} onChange={e => setFormData({ ...formData, outTime: e.target.value })} /></Grid>

                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Parents Name" size="small" value={formData.parentsName} onChange={e => setFormData({ ...formData, parentsName: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Relation with Student" size="small" value={formData.relation} onChange={e => setFormData({ ...formData, relation: e.target.value })} /></Grid>

                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Parents Vehicle No" size="small" value={formData.vehicleNo} onChange={e => setFormData({ ...formData, vehicleNo: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Parents Aadhaar No" size="small" value={formData.parentsAdhar} onChange={e => setFormData({ ...formData, parentsAdhar: e.target.value })} /></Grid>

                        <Grid className="col-sm-4 mb-3" item xs={6}><TextField fullWidth label="Emergency Contact No" size="small" value={formData.emergencyNo} onChange={e => setFormData({ ...formData, emergencyNo: e.target.value })} /></Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12}><TextField fullWidth label="Address" multiline rows={2} size="small" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                    <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSave} disabled={loading}>Save In-Pass</Button>
                </DialogActions>
            </Dialog>

            <div id="print-area">
                {printData && (
                    <div className="pass-container" style={{
                        width: '310mm', // Standard A4 Width
                        minHeight: '130mm',
                        padding: '0', // Remove padding to allow for the colored sidebar
                        backgroundColor: 'white',
                        margin: '20px auto',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        display: 'flex',
                        overflow: 'hidden',
                        fontFamily: "'Inter', sans-serif",
                        position: 'relative'
                    }}>
                        {/* Modern Vertical Sidebar */}
                        <Box sx={{
                            width: '60px',
                            bgcolor: '#0f172a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            color: 'white'
                        }}>
                            <Typography variant="h6" sx={{ letterSpacing: '4px', fontWeight: 'bold', opacity: 0.8 }}>
                                HOSTEL IN-PASS
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, p: 4 }}>
                            {/* Header Section */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, borderBottom: '2px solid #f1f5f9', pb: 2 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: '600', letterSpacing: '1px' }}>
                                        ABHINAV FOUNDATION'S
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>
                                        MOHAN MALI <span style={{ color: '#0d9488' }}>INTERNATIONAL</span> SCHOOL
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        Kavathe Mahankal, Sangli | Maharashtra
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" sx={{ fontWeight: '900', color: '#0d9488' }}>
                                        #{printData.passNo}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: '4px' }}>
                                        ENTRY PASS
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Primary Info Grid */}
                            <Grid container spacing={3}>
                                {/* Time & Date Strip */}
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={4} sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">ARRIVAL DATE</Typography>
                                            <Typography variant="body1" fontWeight="bold">{printData.date} ({printData.day})</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">IN TIME</Typography>
                                            <Typography variant="body1" fontWeight="bold">{printData.outTime}</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                {/* Student Details */}
                                <Grid item xs={7}>
                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>STUDENT INFORMATION</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: '700', color: '#1e293b' }}>{printData.studentName}</Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>Class: <b>{printData.gradeClass}</b></Typography>

                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>PARENT/GUARDIAN DETAILS</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: '600' }}>{printData.parentsName} <span style={{ fontSize: '12px', color: '#94a3b8' }}>({printData.relation})</span></Typography>
                                    <Typography variant="body2" color="textSecondary">Contact: {printData.parentsContact}</Typography>
                                    <Typography variant="body2" color="textSecondary">Aadhaar: [Redacted]</Typography>
                                </Grid>

                                {/* Logistics Details */}
                                <Grid item xs={5} sx={{ borderLeft: '1px dashed #cbd5e1', pl: 3 }}>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary">VEHICLE NO.</Typography>
                                        <Typography variant="body2" fontWeight="bold">{printData.vehicleNo || 'N/A'}</Typography>
                                    </Box>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary">EMERGENCY CONTACT</Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#e11d48' }}>{printData.emergencyNo}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">ADDRESS</Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{printData.address}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Signature Section */}
                            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Box sx={{ textAlign: 'center', width: '150px' }}>
                                        <Box sx={{ height: '40px' }} /> {/* Space for actual sign */}
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            PARENT SIGN
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', width: '150px' }}>
                                        <Box sx={{ height: '40px' }} />
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            STUDENT SIGN
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center', width: '180px' }}>
                                        <Box sx={{ height: '40px' }} />
                                        <Typography variant="caption" sx={{ display: 'block', borderTop: '1px solid #cbd5e1', pt: 1, fontWeight: 'bold' }}>
                                            AUTHORIZED SIGNATORY
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Footer Badges */}
                            <Stack direction="row" spacing={2} justifyContent="center" mt={4} sx={{ opacity: 0.6 }}>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>WARDEN</Typography>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>ACCOUNTS</Typography>
                                <Typography variant="caption" sx={{ border: '1px solid #ddd', px: 1, borderRadius: '4px' }}>OFFICE</Typography>
                            </Stack>
                        </Box>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Studentinpass;